// ─────────────────────────────────────────────────────────────────────────────
// Backend errors to UI text map for the error modal of every placement screen
// (ActionsMarketView, ActionsAmerican, ActionsCartTerminal) and for the parent
// bridge (SPORT_BET_PLACED errorTitle / errorMessage), so that the embedding page
// gets the same text the player sees.
// Pure function: easy to test and maintain. To add a new error, just add a key to the map.
//
// `vue` is anything with `$t(key, values)`: the component itself, or { $t } for the bridge.
//
// New required I18n keys (to be added to translation files):
//   - errors.oddDisabled              → "Odd not available"
//   - errors.oddDisabledReason        → "One or more odds in the cart have been disabled."
//   - errors.multiplicityChanged      → "Multiplicity updated"
//   - errors.multiplicityReason       → "The multiplicity of an odd has changed. Check your cart."
//   - errors.oddSuspended             → "Odd suspended"
//   - errors.oddSuspendedReason       → "This odd is momentarily not playable."
//   - errors.unsupportedLiveEvent     → "Live bet not allowed"
//   - errors.unsupportedLiveEventReason → "This event is only available for prematch betting."
// ─────────────────────────────────────────────────────────────────────────────
export function resolveErrorDisplay(vue, response) {
  const errorCode = response && response.error_code;

  // external_save_failed: the backend sends a descriptive message in response.reason.
  // The title (code) is always i18n; the body (reason) uses the raw backend text
  // because it may contain specific non-localizable details (e.g. "Odds range error").
  // If response.reason is missing, the i18n fallback is used.
  if (errorCode === 'external_save_failed') {
    return {
      code: response.reason === 'Errore range di quote non rispettate'
          ? vue.$t('limitExceed')
          : vue.$t('bet_not_accepted'),
      reason: response.reason || vue.$t('pleaseTryAgain'),
    };
  }

  // MAX_ODDS_REACHED: backend enforces cart-level max_odds. response.data carries
  // { maxOdds, oddsTotal, betType }. We pass them to i18n so the user sees the
  // actual numbers and a suggestion to remove selections.
  // Note: platforms commonly set max_odds = "0" / 0 / "" to mean "no limit",
  // so the backend never throws this code in that case — only strictly
  // positive values trigger it.
  if (errorCode === 'MAX_ODDS_REACHED') {
    const data = (response && response.data) || {};
    return {
      code: vue.$t('errors.maxOddsReached'),
      reason: vue.$t('errors.maxOddsReachedReason', {
        oddsTotal: data.oddsTotal != null ? data.oddsTotal : '-',
        maxOdds: data.maxOdds != null ? data.maxOdds : '-',
      }),
    };
  }

  // AUTO_RECHARGE_FAILED: set by the cart, not by the backend. Playing on behalf of a player with the
  // automatic recharge on, the top up of the player's balance failed and the bet was not placed.
  // response.reason is the payments message (e.g. "Pin error"), when there is one.
  if (errorCode === 'AUTO_RECHARGE_FAILED') {
    const reason = vue.$t('errors.autoRechargeFailedReason');
    return {
      code: vue.$t('errors.autoRechargeFailed'),
      reason: response.reason ? `${reason} (${response.reason})` : reason,
    };
  }

  const map = {
    totals_incorrect:                 { code: vue.$t('bet_not_accepted'),                  reason: vue.$t('pleaseTryAgain') },
    vincols_check:                    { code: vue.$t('bet_not_accepted'),                  reason: vue.$t('pleaseTryAgain') },
    vincols_repetition:               { code: vue.$t('bet_not_accepted'),                  reason: vue.$t('vincols_repetitions') },
    review:                           { code: vue.$t('betReview'),                         reason: vue.$t('betUnderReview') },
    event_not_avaiable:               { code: vue.$t('bet_not_accepted'),                  reason: vue.$t('oddNotAvailableInCart') },
    // For user_locked and agency_locked: code = title, reason = message body.
    // Add user_locked_reason / agency_locked_reason i18n keys to translation files.
    user_locked:                      { code: vue.$t('user_locked'),                       reason: vue.$t('user_locked_reason') },
    agency_locked:                    { code: vue.$t('agency_locked'),                     reason: vue.$t('agency_locked_reason') },
    MAX_CURRENT_WEEK_PLAYABLE:        { code: vue.$t('errors.MAX_CURRENT_WEEK_PLAYABLE'),  reason: vue.$t('pleaseTryAgain') },
    MAX_CURRENT_DAY_PLAYABLE:         { code: vue.$t('errors.MAX_CURRENT_DAY_PLAYABLE'),   reason: vue.$t('pleaseTryAgain') },
    user_profile_forbidden:           { code: vue.$t('errors.userProfileNotEnabled'),      reason: vue.$t('pleaseTryAgain') },
    INVALID_CURRENCY:                 { code: vue.$t('errors.systemError'),                reason: vue.$t('pleaseTryAgain') },
    reject_bet_time_live_odd_expired: { code: vue.$t('bet_not_accepted'),                  reason: vue.$t('marketClosedPleaseTryAgain') },
    cant_book_live_odds:              { code: vue.$t('cant_book_live_odds'),                reason: vue.$t('pleaseTryAgain') },
    // Duplicate selection: the cart submitted the same outcome twice (with slightly
    // different unique_id). The backend rejects the bet up-front; we ask the user
    // to remove the duplicates without exposing the internal id.
    DUPLICATE_OUTCOME_ID:             { code: vue.$t('errors.duplicateOutcomeId'),         reason: vue.$t('errors.duplicateOutcomeIdReason') },
    // Odd integrity errors: expired odd / not updated / invalid value
    time_over:                        { code: vue.$t('bet_not_accepted'),                  reason: vue.$t('oddNotAvailableInCart') },
    last_update_over:                 { code: vue.$t('bet_not_accepted'),                  reason: vue.$t('marketClosedPleaseTryAgain') },
    invalid_odd:                      { code: vue.$t('bet_not_accepted'),                  reason: vue.$t('oddNotAvailableInCart') },
    invalid_outcome_value:            { code: vue.$t('bet_not_accepted'),                  reason: vue.$t('oddNotAvailableInCart') },
    // Odd status / multiplicity errors
    ODD_DISABLED:                     { code: vue.$t('errors.oddDisabled'),                    reason: vue.$t('errors.oddDisabledReason') },
    // Selection closed in the feed (settled, cancelled, interrupted, abandoned, coverage lost):
    // permanent, retrying does not help. rocky-bets oddIntegrityRule, no `reason` in the answer.
    ODD_INVALID_STATUS:               { code: vue.$t('errors.oddClosed'),                      reason: vue.$t('errors.oddClosedReason') },
    ODD_SUSPENDED:                    { code: vue.$t('errors.oddSuspended'),                   reason: vue.$t('errors.oddSuspendedReason') },
    UNSUPPORTED_LIVE_EVENT:           { code: vue.$t('errors.unsupportedLiveEvent'),           reason: vue.$t('errors.unsupportedLiveEventReason') },
    ODD_MULTIPLICITY_ERROR:           { code: vue.$t('errors.multiplicityChanged'),            reason: vue.$t('errors.multiplicityReason') },
    CHANGED_ODD_VALUE:                { code: vue.$t('errors.oddChanged'),                     reason: vue.$t('errors.oddChangedReason') },
    PRODUCT_OFFLINE:                  { code: vue.$t('errors.productOffline'),                 reason: vue.$t('errors.productOfflineReason') },
    MULTIPLE_BET_BUILDER_SAME_EVENT:  { code: vue.$t('errors.multipleBetBuilderSameEvent'),    reason: vue.$t('errors.multipleBetBuilderSameEventReason') },
    // Bet builder failures: BET_BUILDER_UNAVAILABLE is transient (5xx from provider after retries),
    // ODD_NOT_FOUND / BET_BUILDER_MULTIPLE_EVENTS are validation errors propagated from the bet
    // builder dispatcher. BET_BUILDER_ERROR remains the fallback for AppErrors without an explicit code.
    BET_BUILDER_UNAVAILABLE:          { code: vue.$t('errors.betBuilderUnavailable'),          reason: vue.$t('errors.betBuilderUnavailableReason') },
    ODD_NOT_FOUND:                    { code: vue.$t('errors.oddNotFound'),                    reason: vue.$t('errors.oddNotFoundReason') },
    BET_BUILDER_MULTIPLE_EVENTS:      { code: vue.$t('errors.betBuilderMultipleEvents'),       reason: vue.$t('errors.betBuilderMultipleEventsReason') },
    BET_BUILDER_ERROR:                { code: vue.$t('errors.betBuilderGeneric'),              reason: vue.$t('errors.betBuilderGenericReason') },
    // Live bet builder: the platform, the engine or the sport cannot price this live event,
    // or the match state cannot be priced right now (temporary).
    BET_BUILDER_EVENT_IN_PROGRESS:    { code: vue.$t('errors.betBuilderEventInProgress'),      reason: vue.$t('errors.betBuilderEventInProgressReason') },
    BET_BUILDER_LIVE_NOT_SUPPORTED:   { code: vue.$t('errors.betBuilderLiveNotSupported'),     reason: vue.$t('errors.betBuilderLiveNotSupportedReason') },
    BET_BUILDER_LIVE_SPORT_NOT_SUPPORTED: { code: vue.$t('errors.betBuilderLiveSportNotSupported'), reason: vue.$t('errors.betBuilderLiveSportNotSupportedReason') },
    BET_BUILDER_LIVE_SCORE_UNAVAILABLE: { code: vue.$t('errors.betBuilderLiveScoreUnavailable'), reason: vue.$t('errors.betBuilderLiveScoreUnavailableReason') },
    // The provider answered but could not price this combination on this event (not an outage).
    BET_BUILDER_COMBINATION_UNAVAILABLE: { code: vue.$t('errors.betBuilderCombinationUnavailable'), reason: vue.$t('errors.betBuilderCombinationUnavailableReason') },
    // A bet builder only in a single or a multiple (rocky-bets refuses it in a system or an integral).
    BET_BUILDER_BET_TYPE_NOT_ALLOWED: { code: vue.$t('errors.betBuilderBetType'), reason: vue.$t('errors.betBuilderBetTypeReason') },
  };

  if(map[errorCode])
    return map[errorCode];
  else if(response.error_code && response.reason){
    return { code: response.error_code, reason: response.reason };
  }
  else
    return { code: vue.$t('errors.systemError'), reason: vue.$t('pleaseTryAgain') }

}
